<template>
    <div data-widget="form-module" class="form-container">
        <form
            :id="`form-${formID}`"
            enctype="multipart/form-data"
            @submit.prevent="sendForm(formID)"
        >
            <div class="main-fields">
                <div
                    v-for="(field, fieldID) in form.children"
                    :key="fieldID"
                    class="col-span-full"
                    :class="{
                        'md:col-span-11': (field.columnWidth / 100 * 12) === 11,
                        'md:col-span-10': (field.columnWidth / 100 * 12) === 10,
                        'md:col-span-9': (field.columnWidth / 100 * 12) === 9,
                        'md:col-span-8': (field.columnWidth / 100 * 12) === 8,
                        'md:col-span-7': (field.columnWidth / 100 * 12) === 7,
                        'md:col-span-6': (field.columnWidth / 100 * 12) === 6,
                        'md:col-span-5': (field.columnWidth / 100 * 12) === 5,
                        'md:col-span-4': (field.columnWidth / 100 * 12) === 4,
                        'md:col-span-3': (field.columnWidth / 100 * 12) === 3
                    }"
                >
                    <span
                        v-if="field.type === 'Text' || field.type === 'Email' || field.type === 'URL'" 
                        class="text-input-container"
                    >
                        <input
                            :id="`${fieldID}`"
                            :name="field[langLabel]"
                            class="text-input" 
                            :type="field.type.toLowerCase()" 
                            :required = field.required
                            placeholder=""
                        >
                        <label :for="`${fieldID}`" class="text-label">{{ field[langLabel] }}<span v-if="field.required">*</span></label>

                        <span class="error-message"></span>
                    </span>

                    <span
                        v-if="field.type === 'Textarea'" 
                        class="text-input-container"
                    >
                        <textarea
                            :id="`${fieldID}`"
                            :name="field[langLabel]"
                            class="textarea text-input" 
                            type="text" 
                            :required = field.required
                            placeholder=""
                        />

                        <label :for="`${fieldID}`" class="text-label">{{ field[langLabel] }}<span v-if="field.required">*</span></label>

                        <span class="error-message"></span>
                    </span>

                    <div v-if="field.type === 'Checkbox'" class="checkbox">
                        <input
                            type="checkbox"
                            :id="`${fieldID}`"
                            :name="field[langLabel]"
                            :required="true"
                        >

                        <label :for="`${fieldID}`"><div class="inline">{{ field[langLabel] }}</div><span v-if="field.required">*</span></label>

                        <span class="error-message"></span>
                    </div>

                    <div
                        v-if="field.type === 'Radios'"
                        class="radio-group"
                    >
                        <span class="radio-group-label">{{ field[langLabel] }}<span v-if="field.required">*</span></span>

                        <div
                            v-for="(option, optionID) in getRadioOptions(field)"
                            :key="`${fieldID}-${optionID}`"
                            class="checkbox"
                        >
                            <input
                                :id="`${fieldID}-${optionID}`"
                                :data-field-id="`${fieldID}`"
                                :data-field-label="field[langLabel]"
                                type="radio"
                                :name="field[langLabel]"
                                :value="option"
                                :required="field.required && optionID === 0"
                            >

                            <label :for="`${fieldID}-${optionID}`"><div class="inline">{{ option }}</div></label>
                        </div>

                        <span class="error-message"></span>
                    </div>

                    <div
                        v-if="field.type === 'FormBuilderFile'"
                        class="file-upload-container"
                    >
                        <label :for="`${fieldID}`">{{ field[langLabel] }}</label>

                        <input
                            class="file-upload"
                            type="file"
                            :id="`${fieldID}`"
                            :name="field[langLabel]"
                            :required="field.required"
                            :accept="`.${field.extensions.replaceAll(' ', ', .')}`"
                        >

                        <span class="error-message"></span>
                    </div>
                </div>
            </div>

            <div class="form-legals">
                <div class="checkbox">
                    <input
                        type="checkbox"
                        id="legal-checkbox"
                        :name="'agb akzeptiert'"
                        :required="true"
                    >

                    <label :for="'legal-checkbox'"><div class="inline" v-html="globals.pagecontent.formTextLegals"></div><span>*</span></label>

                    <span class="error-message"></span>
                </div>
            </div>

            <div class="candy-bag">
                <input type="text" id="input-chocolate-chips">
                <input type="text" id="input-gummy-bears" value="this-is-fine">
            </div>

            <div class="form-submit">
                <FadeInTransition>
                    <button
                        v-show="!showPreloader"
                        class="button cta--default flex-row inline-flex"
                        :class="{'disabled': formSent}"
                    >
                        <span class="label">{{ globals.pagecontent.submitForm }}</span>
                        <span class="basis-auto">
                            <div class="circle circle-default"></div>
                        </span>
                    </button>
                </FadeInTransition>

                <FadeInTransition>
                    <span 
                        v-if="formSent && !showPreloader" 
                        class="success-msg col-span-full block mt-1"
                    >{{ form[langSuccess] }}</span>
                </FadeInTransition>

                <FadeInTransition>
                    <PreloaderIcon v-if="showPreloader" />
                </FadeInTransition>
            </div>
        </form>
    </div>
</template>

<script setup lang="ts">
    import { useRequestStore } from '@/stores/requests'
    import { type AuthResponse } from '@/types/AuthTypes'
    import { nanoid } from 'nanoid'
    import { FormHandler } from "~/composables/Classes/FormHandler"

    const props = defineProps<{
        pID?: number,
        givenFormID?: number
    }>()

    const { locale } = useI18n()

    const requestStore = useRequestStore()
    const { pageContent, pageID, form, pageIndex, globals } = storeToRefs(requestStore)

    const showPreloader = ref<boolean>(false)
    const formSent = ref<boolean>(false)
    const formID = ref<number>()

    let formData: any

    const getForm = async (formID: any) => {
        try {
            formData = await useBackendApiGet<AuthResponse>(`v1/form-data/${formID}/${locale.value}`);

            console.log(formData)

            if (formData) {
                requestStore.fillPageform(formData)
            }
        } catch (error) {
            console.error(error)
        }
    }

    const initForm = () => {
        if (props.givenFormID) {
            formID.value = props.givenFormID
        } else {
            const widgets = pageContent.value[pageID.value]?.pagecontent.widgets
            const widgetContent = widgets?.find((widget: any) => widget.meta.cID === props.pID)
            formID.value = widgetContent.meta.form[0].formID
        }

        getForm(formID.value)
    }

    const langLabel = computed(() => {
        const langID = pageIndex.value.global_meta.langID
        const label = langID === 1017 ? 'label' : `label${langID}`

        return label
    })

    const langSuccess = computed(() => {
        const langID = pageIndex.value.global_meta.langID
        const successMsg = langID === 1017 ? 'successMessage' : `successMessage${langID}`

        return successMsg
    })

    const normalizeMailValue = (element: any) => {
        if (element.type === 'file') {
            const files = Array.from(element.files || []) as File[]

            return files.map((file) => file.name).join(', ')
        }

        if (element.type === 'checkbox') {
            return locale.value === 'de'
                ? (element.checked ? 'Ja' : 'Nein')
                : (element.checked ? 'Yes' : 'No')
        }

        return element.value
    }

    const parseRecipients = (emailTo: unknown) => {
        if (typeof emailTo !== 'string') {
            return []
        }

        return emailTo
            .split(/\r?\n/)
            .map((recipient) => recipient.trim())
            .filter(Boolean)
    }

    const getRadioOptions = (field: any) => {
        const langID = pageIndex.value.global_meta.langID
        const optionsKey = langID === 1017 ? 'options' : `options${langID}`
        const options = field?.[optionsKey] || field?.options || ''

        return String(options)
            .split(/\r?\n/)
            .map((option) => option.trim())
            .filter(Boolean)
    }

    const collectFileAttachments = (elements: any[]) => {
        return elements.flatMap((element) => {
            if (element.type !== 'file') {
                return []
            }

            const files = Array.from(element.files || []) as File[]

            return files.map((file) => ({
                fieldId: element.id,
                fieldLabel: element.name,
                file
            }))
        })
    }

    const buildMailPayload = (elements: any[], currentFormID: number, emailTo: unknown) => {
        const fields = elements
            .filter((element) => {
                if (!element.name || element.id === 'input-chocolate-chips' || element.id === 'input-gummy-bears') {
                    return false
                }

                if (element.type === 'radio') {
                    return element.checked
                }

                return true
            })
            .map((element) => ({
                id: element.dataset.fieldId || element.id,
                label: element.dataset.fieldLabel || element.name,
                type: element.type,
                value: normalizeMailValue(element)
            }))

        const replyToField = fields.find((field) => field.type === 'email' && field.value)
        const formTitle = pageContent.value[pageID.value]?.pagemeta?.title || `Formular ${currentFormID}`

        return {
            recipients: parseRecipients(emailTo),
            subject: `Neue Formularanfrage: ${formTitle}`,
            formID: currentFormID,
            messageID: nanoid(6),
            lang: locale.value,
            replyTo: replyToField?.value || '',
            autoResponse: replyToField?.value
                ? {
                    recipient: replyToField.value,
                    subject: locale.value === 'de'
                        ? (formData?.responderSubject1021 || '')
                        : (formData?.responderSubject || ''),
                    body: locale.value === 'de'
                        ? (formData?.responderBody1021 || '')
                        : (formData?.responderBody || '')
                }
                : null,
            fields
        }
    }

    const sendForm = async (formID: any) => {
        const formElement: any = document.querySelector('#form-' + formID)
        const elements = formElement.elements
        const elementsArray = Array.prototype.slice.call(elements)
        
        showPreloader.value = true

        const jsonData: any = {}

        elementsArray.forEach(element => {
            switch (element.type) {
                case 'email':
                case 'text':
                case 'textarea':
                    jsonData[element.id] = element.value
                    break
                case 'radio':
                    if (element.checked) {
                        jsonData[element.dataset.fieldId || element.id] = element.value
                    }
                    break
                case 'checkbox':
                    jsonData[element.id] = element.checked
                    break
                case 'submit':
                    break
            }
        })

        FormHandler.validateOnSubmit(elementsArray)

        if(formElement.getElementsByClassName('input-error').length > 0) {
            showPreloader.value = false
            return false
        }

        const messageID = nanoid(6)
        const attachments = collectFileAttachments(elementsArray)

        jsonData['messageID'] = messageID
        jsonData['formID'] = formID;

        console.log(jsonData)

        const formStatus = await FormHandler.sendFormData(jsonData, formID, locale.value)
        const mailRecipients = parseRecipients(formData?.emailTo)

        switch (formStatus) {
            case '200':
                if (mailRecipients.length > 0) {
                    try {
                        await FormHandler.sendFormMail(
                            {
                                ...buildMailPayload(elementsArray, formID, formData?.emailTo),
                                messageID
                            },
                            attachments
                        )
                    } catch (error) {
                        console.error(error)
                        showPreloader.value = false
                        formElement.querySelector('.form-submit button').classList.remove('disabled')
                        formElement.querySelector('.form-submit button').classList.add('error')
                        return
                    }
                }

                formSent.value = true
                
                setTimeout(() => {
                    showPreloader.value = false
                }, 2000)
                break;
            case '400':
            case '404':
            case '405':
            case '500':
                formElement.querySelector('.form-submit button').classList.remove('disabled')
                formElement.querySelector('.form-submit button').classList.add('error')
                break;
        }
    }

    initForm()
</script>